import {Injectable} from '@angular/core';
import {from, Observable, Subject, tap} from 'rxjs';
import {TokenStorageService} from './jwt/token-storage.service';
import {User} from '../user/user';
import {LoggerService} from '../logger/logger.service';
import {LoginRestApiService} from './http/login-rest-api.service';
import {AuthenticationResponse} from './http/authentication-response';
import {AccessTokenPayload} from './jwt/access-token-payload';
import {OnAppInitInterface, Utils} from '@astek/utils';

@Injectable()
export class AuthenticationService implements OnAppInitInterface<Observable<void>> {

  private _user: User | null = null;

  private _authenticatedSubject: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly _loggerService: LoggerService,
    private readonly _tokenStorageService: TokenStorageService,
    private readonly _loginRestApiService: LoginRestApiService,
  ) {

  }

  public get isAuthenticated(): boolean {
    return this._user !== null;
  }

  public get user(): User | null {
    return this._user;
  }

  public init(): void {
  }

  public authenticationChanged(): Observable<boolean> {
    return this._authenticatedSubject.asObservable();
  }

  public login(login: string, password: string): Observable<AuthenticationResponse> {
    this.logout();
    return this._loginRestApiService.requestAuthenticationTokens(login, password).pipe(
      tap((response: AuthenticationResponse) => {
        if (Utils.isNotNullOrUndefined(response.accessToken)) {
          this.saveTokensAndSetupAuth(response);
        } else if (Utils.isNotNullOrUndefined(response.preAuthToken)) {
          this.savePreAuthenticationToken(response);
        }
      }),
    );
  }

  public checkDualFactorCode(value: string): Observable<AuthenticationResponse> {
    return this._loginRestApiService.request2FACheck(value).pipe(
      tap((response: AuthenticationResponse) => {
        this.saveTokensAndSetupAuth(response);
      }),
    );
  }

  public resendDualFactorCode(): Observable<AuthenticationResponse> {
    return this._loginRestApiService.requestResend2FACode().pipe(
      tap((response: AuthenticationResponse) => this.savePreAuthenticationToken(response)),
    );
  }

  public logout(): void {
    this._tokenStorageService.deleteAccessToken();
    this._user = null;
    this._authenticatedSubject.next(this.isAuthenticated);
  }

  public onAppInit(): Observable<void> {
    return from(this.tryAuthenticateFromToken());
  }

  private savePreAuthenticationToken(authenticationResponse: AuthenticationResponse): void {
    if (Utils.isNullOrUndefined(authenticationResponse.preAuthToken)) {
      return;
    }
    this._loggerService.debug('Save preAuth token', authenticationResponse);
    this._tokenStorageService.setPreAccessToken(authenticationResponse.preAuthToken);
  }

  private saveTokensAndSetupAuth(authenticationResponse: AuthenticationResponse): void {
    if (Utils.isNullOrUndefined(authenticationResponse.accessToken)) {
      return;
    }
    this._loggerService.debug('Save auth token', authenticationResponse);
    this._tokenStorageService.writeAccessToken(authenticationResponse.accessToken);
    const accessTokenPayload: AccessTokenPayload | null = this._tokenStorageService.extractPayload(
      authenticationResponse.accessToken,
    );
    if (this._tokenStorageService.isValidTokenPayload(accessTokenPayload)) {
      this.setupAuthentication(accessTokenPayload);
      this._tokenStorageService.writeAccessTokenPayload(accessTokenPayload);
    }
  }

  private setupAuthentication(payload: AccessTokenPayload): void {
    this._user = (new User()).fromPayload(payload);
    this._loggerService.debug('Authentication setup', this._user);
    this._authenticatedSubject.next(this.isAuthenticated);
  }

  private tryAuthenticateFromToken(): Promise<void> {
    const accessToken: string | null = this._tokenStorageService.readAccessToken();
    if (Utils.isNullOrUndefined(accessToken)) {
      this._loggerService.debug('No authentication token found');
      return Promise.resolve();
    }
    const accessTokenPayload: AccessTokenPayload | null = this._tokenStorageService.extractPayload(accessToken);
    if (Utils.isNullOrUndefined(accessTokenPayload)) {
      this._loggerService.info('Invalid access token format', accessTokenPayload);
      // use this method in case another browser tab has refreshed the token
      this._tokenStorageService.deleteAccessTokenIfMatches(accessToken);
    }
    this._loggerService.debug('accessToken', accessTokenPayload);
    const remainingValidity = this._tokenStorageService.computeRemainingValidity(accessTokenPayload);
    if (this._tokenStorageService.isValidTokenPayload(accessTokenPayload) && remainingValidity > 0) {
      this._tokenStorageService.writeAccessTokenPayload(accessTokenPayload);
      this.setupAuthentication(accessTokenPayload);
      // SUCCESS
      return Promise.resolve();
    }

    this.logout();
    return Promise.resolve();
  }
}
